import React, { useCallback, useEffect, useRef, useState } from 'react';
import { apiFetch } from '../lib/apiFetch';
import { toast } from '../lib/toast';
import { useSocket } from '../context/SocketContext';
import {
    ApiResponse, ListaLlamadasVenta, LlamadaVenta, Cliente,
    ESTADO_LLAMADA_LABELS, ESTADO_LLAMADA_COLORS, ESTADOS_LLAMADA_INTERMEDIOS,
    TEMPERATURA_LEAD_LABELS, TEMPERATURA_LEAD_COLORS, METRICAS_RENDIMIENTO_LABELS
} from '../types';
import { SubirLlamadaVentaModal } from './SubirLlamadaVentaModal';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';
const POLLING_MS = 10000;

const formatFecha = (iso: string): string => {
    const d = new Date(iso);
    return d.toLocaleString('es-AR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
};

const PuntajeBarra: React.FC<{ label: string; valor: number }> = ({ label, valor }) => {
    const color = valor >= 7 ? 'bg-emerald-500' : valor >= 4 ? 'bg-amber-500' : 'bg-red-500';
    return (
        <div>
            <div className="flex justify-between text-xs text-slate-600 mb-0.5">
                <span>{label}</span>
                <span className="font-semibold">{valor}/10</span>
            </div>
            <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden">
                <div className={`h-full ${color}`} style={{ width: `${valor * 10}%` }} />
            </div>
        </div>
    );
};

const LlamadaCard: React.FC<{
    llamada: LlamadaVenta;
    onDescartar: (id: number, version: number) => void;
    onReprocesar: (id: number, version: number) => void;
    onEliminar: (id: number) => void;
}> = ({ llamada, onDescartar, onReprocesar, onEliminar }) => {
    const [abierto, setAbierto] = useState(false);
    const analisis = llamada.analisis;

    return (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            <button
                className="w-full text-left p-4 flex items-center gap-3 hover:bg-slate-50"
                onClick={() => setAbierto(v => !v)}
            >
                <span
                    className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                    style={{ backgroundColor: llamada.cliente_color || '#94a3b8' }}
                />
                <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold text-slate-800 truncate">{llamada.empresa}</div>
                    <div className="text-xs text-slate-500">
                        {formatFecha(llamada.creado_en)}
                        {llamada.producto_servicio ? ` · ${llamada.producto_servicio}` : ''}
                    </div>
                </div>
                {analisis && (
                    <span className={`text-xs px-2 py-1 rounded-full font-medium ${TEMPERATURA_LEAD_COLORS[analisis.temperatura_lead]}`}>
                        {TEMPERATURA_LEAD_LABELS[analisis.temperatura_lead]}
                    </span>
                )}
                <span className={`text-xs px-2 py-1 rounded-full font-medium ${ESTADO_LLAMADA_COLORS[llamada.estado]}`}>
                    {ESTADO_LLAMADA_LABELS[llamada.estado]}
                    {ESTADOS_LLAMADA_INTERMEDIOS.includes(llamada.estado) ? '…' : ''}
                </span>
            </button>

            {abierto && (
                <div className="border-t border-slate-100 p-4 space-y-4">
                    {llamada.estado === 'error' && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
                            {llamada.error_detalle || 'Error al procesar la llamada'}
                            <button
                                onClick={() => onReprocesar(llamada.id, llamada.version)}
                                className="ml-3 underline font-medium"
                            >
                                Reintentar
                            </button>
                        </div>
                    )}

                    {analisis && (
                        <>
                            <p className="text-sm text-slate-700">{analisis.resumen_conversacion}</p>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                {Object.entries(METRICAS_RENDIMIENTO_LABELS).map(([key, label]) => (
                                    <PuntajeBarra key={key} label={label} valor={(analisis as any)[key]} />
                                ))}
                            </div>

                            {analisis.puntos_fuertes.length > 0 && (
                                <div>
                                    <h4 className="text-xs font-semibold text-emerald-700 mb-1.5">✅ Puntos fuertes</h4>
                                    <ul className="text-sm text-slate-700 space-y-1 list-disc list-inside">
                                        {analisis.puntos_fuertes.map((p, i) => <li key={i}>{p}</li>)}
                                    </ul>
                                </div>
                            )}

                            {analisis.errores_criticos.length > 0 && (
                                <div>
                                    <h4 className="text-xs font-semibold text-red-700 mb-1.5">⚠️ Errores críticos</h4>
                                    <div className="space-y-2">
                                        {analisis.errores_criticos.map((e, i) => (
                                            <div key={i} className="bg-red-50 border border-red-100 rounded-lg p-2.5 text-sm">
                                                <div className="font-medium text-red-800">{e.tipo}</div>
                                                <div className="text-slate-700 mt-0.5">{e.descripcion}</div>
                                                <div className="text-slate-500 mt-1 italic">"{e.momento_transcripcion}"</div>
                                                <div className="text-emerald-700 mt-1">→ {e.correccion_sugerida}</div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {analisis.oportunidades_cierre_perdidas.length > 0 && (
                                <div>
                                    <h4 className="text-xs font-semibold text-amber-700 mb-1.5">🎯 Oportunidades de cierre perdidas</h4>
                                    <ul className="text-sm text-slate-700 space-y-1 list-disc list-inside">
                                        {analisis.oportunidades_cierre_perdidas.map((o, i) => <li key={i}>{o}</li>)}
                                    </ul>
                                </div>
                            )}

                            {analisis.plan_accion_vendedor.length > 0 && (
                                <div>
                                    <h4 className="text-xs font-semibold text-blue-700 mb-1.5">📌 Plan de acción</h4>
                                    <ul className="text-sm text-slate-700 space-y-1 list-disc list-inside">
                                        {analisis.plan_accion_vendedor.map((a, i) => <li key={i}>{a}</li>)}
                                    </ul>
                                </div>
                            )}

                            <details className="text-xs text-slate-500">
                                <summary className="cursor-pointer select-none">Ver transcripción completa</summary>
                                <p className="mt-2 whitespace-pre-wrap">{analisis.transcripcion}</p>
                            </details>
                        </>
                    )}

                    <div className="flex justify-end gap-4 pt-2 border-t border-slate-100">
                        <button
                            onClick={() => onDescartar(llamada.id, llamada.version)}
                            className="text-xs text-slate-500 hover:text-red-600"
                        >
                            Descartar
                        </button>
                        <button
                            onClick={() => onEliminar(llamada.id)}
                            className="text-xs text-red-600 hover:text-red-700 font-medium"
                            title="Eliminar de forma permanente (para llamadas cargadas por error)"
                        >
                            🗑️ Eliminar
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

const POR_PAGINA = 20;

export const VistaLlamadasVenta: React.FC = () => {
    const [data, setData] = useState<ListaLlamadasVenta | null>(null);
    const [clientes, setClientes] = useState<Cliente[]>([]);
    const [filtroCliente, setFiltroCliente] = useState<string>('');
    const [pagina, setPagina] = useState(1);
    const [mostrarSubir, setMostrarSubir] = useState(false);
    const { on, off } = useSocket();
    const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

    // Mismo bug que tenía VistaAudios.tsx: el backend pagina de entrada
    // (por_pagina default 20) pero nunca se lo pedíamos ni mostrábamos que
    // hubiera más de una página — con más de 20 llamadas el resto hubiera
    // quedado inaccesible sin ningún aviso. Hoy la cuenta tiene 0 llamadas
    // así que no mordía todavía, pero es el mismo problema latente.
    const fetchLlamadas = useCallback(async () => {
        const params = new URLSearchParams();
        if (filtroCliente) params.set('cliente_id', filtroCliente);
        params.set('pagina', String(pagina));
        params.set('por_pagina', String(POR_PAGINA));
        try {
            const res = await apiFetch(`${API_BASE}/llamadas-venta?${params.toString()}`);
            const json: ApiResponse<ListaLlamadasVenta> = await res.json();
            if (json.success && json.data) setData(json.data);
        } catch (e) {
            console.error(e);
        }
    }, [filtroCliente, pagina]);

    useEffect(() => { setPagina(1); }, [filtroCliente]);

    const fetchClientes = useCallback(async () => {
        try {
            const res = await apiFetch(`${API_BASE}/clientes`);
            const json: ApiResponse<Cliente[]> = await res.json();
            if (json.success && json.data) setClientes(json.data);
        } catch (e) {
            console.error(e);
        }
    }, []);

    useEffect(() => {
        fetchClientes();
    }, [fetchClientes]);

    useEffect(() => {
        fetchLlamadas();
        intervalRef.current = setInterval(fetchLlamadas, POLLING_MS);
        return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
    }, [fetchLlamadas]);

    // Refresco inmediato cuando el worker termina de analizar, en vez de
    // esperar hasta 10s del polling — mismo patrón que WSListener en App.tsx.
    useEffect(() => {
        const refresh = () => fetchLlamadas();
        on('llamada:analizada', refresh);
        on('llamada:actualizada', refresh);
        return () => {
            off('llamada:analizada', refresh);
            off('llamada:actualizada', refresh);
        };
    }, [on, off, fetchLlamadas]);

    const handleDescartar = async (id: number, version: number) => {
        const confirmado = await toast.confirm({
            title: '¿Descartar esta llamada?',
            confirmText: 'Descartar',
            type: 'warning'
        });
        if (!confirmado) return;
        try {
            const res = await apiFetch(`${API_BASE}/llamadas-venta/${id}/descartar`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ version })
            });
            const json: ApiResponse<null> = await res.json();
            if (json.success) {
                toast.success('Llamada descartada');
                fetchLlamadas();
            } else {
                toast.error(json.error === 'VERSION_MISMATCH' ? 'El registro cambió, recargá' : (json.error || 'Error'));
            }
        } catch {
            toast.error('No se pudo conectar con el servidor');
        }
    };

    // Borrado permanente (fila + archivo) — distinto de "Descartar" (soft
    // delete). No lleva `version` porque no es una edición concurrente
    // sujeta a CAS, es una baja definitiva.
    const handleEliminar = async (id: number) => {
        const confirmado = await toast.confirm({
            title: '¿Eliminar esta llamada?',
            description: 'Se borra el registro y el archivo de forma permanente. No se puede deshacer.',
            confirmText: 'Eliminar',
            type: 'warning'
        });
        if (!confirmado) return;
        try {
            const res = await apiFetch(`${API_BASE}/llamadas-venta/${id}`, { method: 'DELETE' });
            if (res.status === 404) {
                toast.error('La llamada ya no existe');
                fetchLlamadas();
                return;
            }
            const json: ApiResponse<null> = await res.json();
            if (json.success) {
                toast.success('Llamada eliminada');
                fetchLlamadas();
            } else {
                toast.error(json.error || 'Error al eliminar la llamada');
            }
        } catch {
            toast.error('No se pudo conectar con el servidor');
        }
    };

    const handleReprocesar = async (id: number, version: number) => {
        try {
            const res = await apiFetch(`${API_BASE}/llamadas-venta/${id}/reprocesar`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ version })
            });
            const json: ApiResponse<null> = await res.json();
            if (json.success) {
                toast.success('En cola para reprocesar');
                fetchLlamadas();
            } else {
                toast.error(json.error === 'VERSION_MISMATCH' ? 'El registro cambió, recargá' : (json.error || 'Error'));
            }
        } catch {
            toast.error('No se pudo conectar con el servidor');
        }
    };

    return (
        <div className="space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                    <h2 className="text-lg font-semibold text-slate-800">
                        Coach de Llamadas de Venta
                        {data && data.total > 0 && (
                            <span className="ml-2 text-sm font-normal text-slate-500">({data.total})</span>
                        )}
                    </h2>
                    <p className="text-sm text-slate-500">Subí la grabación de una llamada y la IA la analiza como coach de ventas.</p>
                </div>
                <button
                    onClick={() => setMostrarSubir(true)}
                    className="px-3 py-1.5 text-sm bg-slate-800 text-white rounded-lg hover:bg-slate-700"
                >
                    + Subir llamada
                </button>
            </div>

            <select
                value={filtroCliente}
                onChange={e => setFiltroCliente(e.target.value)}
                className="text-sm border border-slate-300 rounded-lg px-3 py-1.5"
            >
                <option value="">Todos los clientes</option>
                {clientes.map(c => (
                    <option key={c.id} value={c.id}>{c.empresa}</option>
                ))}
            </select>

            <div className="space-y-2">
                {data?.items.length === 0 && (
                    <p className="text-sm text-slate-500 text-center py-8">Todavía no se subió ninguna llamada.</p>
                )}
                {data?.items.map(llamada => (
                    <LlamadaCard
                        key={llamada.id}
                        llamada={llamada}
                        onDescartar={handleDescartar}
                        onReprocesar={handleReprocesar}
                        onEliminar={handleEliminar}
                    />
                ))}
            </div>

            {data && data.total > POR_PAGINA && (
                <div className="flex items-center justify-between px-1 text-sm">
                    <span className="text-slate-500">
                        {(pagina - 1) * POR_PAGINA + 1}–{Math.min(pagina * POR_PAGINA, data.total)} de {data.total}
                    </span>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => setPagina(p => Math.max(1, p - 1))}
                            disabled={pagina === 1}
                            className="px-3 py-1.5 text-xs bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                            ← Anterior
                        </button>
                        <span className="text-xs text-slate-500">
                            Página {pagina} de {Math.ceil(data.total / POR_PAGINA)}
                        </span>
                        <button
                            onClick={() => setPagina(p => p + 1)}
                            disabled={pagina * POR_PAGINA >= data.total}
                            className="px-3 py-1.5 text-xs bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                            Siguiente →
                        </button>
                    </div>
                </div>
            )}

            {mostrarSubir && (
                <SubirLlamadaVentaModal
                    clientes={clientes}
                    onClose={() => setMostrarSubir(false)}
                    onSubido={() => { setMostrarSubir(false); fetchLlamadas(); }}
                />
            )}
        </div>
    );
};
